import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faArrowLeft, faSave, faList, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { createProvider, updateProvider, fetchProvider, clearCurrentProvider } from '../../../../store/slices/providerSlice';
import providerService from '../../../../services/providerService';
import { fetchPractice, addProviderOptimistic, fetchPracticePatients } from '../../../../store/slices/practiceSlice';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import { toast } from 'react-toastify';
import { VALIDATION_SUMMARY_MESSAGE, preserveAvailabilityErrors, clearFieldError, validateRequiredPhone, updateFieldErrorOnChange } from '../../../../utils/formValidation';
import { formatUSPhoneNumber, extractPhoneDigits, getPhoneTypingError } from '../../../../utils/phoneUtils';
import PhoneNumberInput from '../../../../components/Common/PhoneNumberInput/PhoneNumberInput';
import { sanitizeEmailInput, getEmailValidationError } from '../../../../utils/emailValidation';
import { sanitizeUsernameInput, getUsernameFormatError, USERNAME_MAX_LENGTH } from '../../../../utils/usernameValidation';
import useUsernameEmailAvailability from '../../../../hooks/useUsernameEmailAvailability';
import './AddProvider.css';
import '../../PatientManagement/AddPatient/AddPatientModal.css';
import api from '../../../../services/api';
import { validatePassword, applyPasswordServerFieldError } from '../../../../utils/passwordValidation';
import AddFormModel from '../../../../components/Common/AddFormModel/AddFormModel';
import FormActionButtons from '../../../../components/Common/FormActionButtons/FormActionButtons';
import FormDetailSkeleton from '../../../../components/SkeletonLoader/FormDetailSkeleton';
import { TIME_ZONES, TITLES, US_STATES, normalizeTimeZone } from '../../../../utils/constants';
import ReassignmentModal from '../../../../components/Common/ReassignmentModal/ReassignmentModal.jsx';

const AddProvider = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { practiceId } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { currentProvider, isLoading, isUpdating, isCreating } = useSelector(state => state.provider);
  const { currentPractice } = useSelector(state => state.practice ?? {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const editId = searchParams.get('editId');
  const isEditMode = Boolean(editId);
  const savedSuccessfullyRef = useRef(false);

  useEffect(() => {
    if (isEditMode && editId) {
      savedSuccessfullyRef.current = false;
    }
  }, [isEditMode, editId]);
  const excludeUserId = isEditMode ? (editId ?? currentProvider?.id ?? null) : null;
  const [loadedCredentials, setLoadedCredentials] = useState({ username: '', email: '' });
  const {
    scheduleUsernameCheck,
    scheduleEmailCheck,
    handleUsernamePaste,
    handleUsernameBlur,
    handleEmailPaste,
    ensureUsernameAvailable,
    ensureEmailAvailable,
  } = useUsernameEmailAvailability({
    excludeUserId,
    originalUsername: loadedCredentials.username,
    originalEmail: loadedCredentials.email,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showReassignmentModal, setShowReassignmentModal] = useState(false);
  const [assignedPatients, setAssignedPatients] = useState([]);
  const [alternativeProviders, setAlternativeProviders] = useState([]);
  const [reassignments, setReassignments] = useState({}); // { patientId: newProviderId }
  const [isReassigning, setIsReassigning] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    middleInitial: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '', // Full Name
    stateId: null,
    zipCode: '',
    homePhone: '',
    cellPhone: '',
    fax: '',
    email: '',
    twoWayAuth: 'Disabled',
    mobileAppAccess: 'Disabled',
    timeZone: '',
    status: 'Active'
  });

  const [initialFormData, setInitialFormData] = useState(null);

  const isChanged = React.useMemo(() => {
    if (!isEditMode || !initialFormData) return true;
    if (formData.password?.trim() || formData.confirmPassword?.trim()) return true;
    return JSON.stringify({ ...formData, password: '', confirmPassword: '' }) !==
      JSON.stringify({ ...initialFormData, password: '', confirmPassword: '' });
  }, [formData, initialFormData, isEditMode]);

  const [errors, setErrors] = useState({});

  const getProviderSubmissionErrorMessage = (error, fallbackMessage) => {
    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallbackMessage
    );
  };

  const applyServerFieldError = (message) => {
    if (!message) return false;

    const normalizedMessage = message.toLowerCase();
    const nextFieldErrors = {};

    if (normalizedMessage.includes('email') && (normalizedMessage.includes('exist') || normalizedMessage.includes('duplicate'))) {
      nextFieldErrors.email = message;
    }

    if (normalizedMessage.includes('username') && (normalizedMessage.includes('exist') || normalizedMessage.includes('duplicate') || normalizedMessage.includes('taken'))) {
      nextFieldErrors.username = message;
    }

    if (normalizedMessage.includes('password')) {
      nextFieldErrors.password = message;
    }

    if (Object.keys(nextFieldErrors).length === 0) {
      return false;
    }

    setErrors(prev => ({ ...prev, ...nextFieldErrors }));
    return true;
  };

  // Fetch provider data when in edit mode
  useEffect(() => {
    if (isEditMode && editId && practiceId) {
      dispatch(clearCurrentProvider());
      dispatch(fetchProvider({ practiceId, providerId: editId }));
    }
  }, [isEditMode, editId, practiceId, dispatch]);

  // Fetch practice data if not already state
  useEffect(() => {
    if (practiceId && (!currentPractice || String(currentPractice.id) !== String(practiceId))) {
      dispatch(fetchPractice(practiceId));
    }
  }, [practiceId, currentPractice, dispatch]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    const practiceName = currentPractice?.practice_name || 'Practice';
    const fromDashboard = !!location.state?.fromDashboard;
    const rootLabel = fromDashboard ? 'Dashboard' : 'Practices';
    const rootPath = fromDashboard ? '/dashboard' : '/practices';
    const dashboardTabSuffix = fromDashboard ? '&fromDashboard=true' : '';


    updateBreadcrumbs([
      { label: rootLabel, path: rootPath },
      { label: practiceName, path: `${fromDashboard ? '/dashboard' : '/practices'}/${practiceId}?tab=practice${dashboardTabSuffix}` },
      { label: 'Providers', path: `${fromDashboard ? '/dashboard' : '/practices'}/${practiceId}?tab=provider${dashboardTabSuffix}` },

      { label: isEditMode ? 'Edit Provider' : 'Add Provider', path: null, active: true }
    ]);

  }, [practiceId, isEditMode, currentPractice, updateBreadcrumbs]);

  // Populate form when provider data is loaded
  useEffect(() => {
    if (isEditMode && currentProvider && String(currentProvider.id) === String(editId)) {
      const stateId = currentProvider.address?.state_id || currentProvider.state_id;
      const state = US_STATES.find(s => s.id === stateId)?.name || '';

      const mappedData = {
        title: (currentProvider.title_id !== null && currentProvider.title_id !== undefined)
          ? String(currentProvider.title_id)
          : (TITLES.find(t => t.label === currentProvider.title)?.id ? String(TITLES.find(t => t.label === currentProvider.title).id) : ''),
        firstName: currentProvider.first_name || '',
        middleInitial: currentProvider.middle_name || '',
        lastName: currentProvider.last_name || '',
        username: currentProvider.username || '',
        password: '',
        confirmPassword: '',
        addressLine1: currentProvider.address?.address_line_1 || currentProvider.address_line_1 || '',
        addressLine2: currentProvider.address?.address_line_2 || currentProvider.address_line_2 || '',
        city: currentProvider.address?.city || currentProvider.city || '',
        state: state,
        stateId: stateId || null,
        zipCode: currentProvider.address?.zip_code || currentProvider.zip_code ? String(currentProvider.address?.zip_code || currentProvider.zip_code) : '',
        homePhone: formatPhoneNumber(currentProvider.cell_phone || ''),
        cellPhone: formatPhoneNumber(currentProvider.phone || ''),
        fax: formatPhoneNumber(currentProvider.fax || ''),
        email: currentProvider.email || '',
        twoWayAuth: Number(currentProvider.is_two_way_auth) === 1 ? 'Enabled' : 'Disabled',
        mobileAppAccess: Number(currentProvider.mobile_app_access) === 1 ? 'Enabled' : 'Disabled',
        timeZone: normalizeTimeZone(currentProvider.time_zone || currentProvider.timeZone || currentProvider.timezone) || '',
        status: currentProvider.status ? (currentProvider.status.charAt(0).toUpperCase() + currentProvider.status.slice(1).toLowerCase()) : 'Active'
      };
      setFormData(mappedData);
      setInitialFormData(mappedData);
      setLoadedCredentials({
        username: mappedData.username || '',
        email: mappedData.email || '',
      });
    }
  }, [isEditMode, currentProvider, editId]);

  // Helper for phone formatting (XXX) XXX - XXXX
  const formatPhoneNumber = formatUSPhoneNumber;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    clearFieldError(setErrors, name);
    let newErrors = { ...errors };

    // Clear existing error for current field before re-validating
    delete newErrors[name];

    // 1. Phone Number Masking & Validation
    if (['homePhone', 'cellPhone', 'fax'].includes(name)) {
      const formatted = value;
      const digits = extractPhoneDigits(formatted);
      setFormData((prev) => ({ ...prev, [name]: formatted }));

      const typingError = getPhoneTypingError(digits, {
        required: name === 'cellPhone',
        requiredLabel: name === 'homePhone' ? 'Home Phone' : name === 'cellPhone' ? 'Cell Phone' : 'Fax',
      });

      setErrors((prev) => {
        const next = { ...prev };
        if (typingError) next[name] = typingError;
        else delete next[name];
        return next;
      });
      return;
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        if (numericValue.length > 0 && numericValue.length < 5) {
          newErrors.zipCode = 'Zip code must be exactly 5 digits';
        } else {
          delete newErrors.zipCode;
        }
        setErrors(newErrors);
      }
      return;
    }

    // State Sync — handled below with error clearing in setFormData

    // 2. Strict Name Validation
    if (['firstName', 'lastName', 'middleInitial'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
      if (value.length > 20) {
        return;
      }
    }

    // 3. Password Validation (Real-time)
    if (name === 'password') {
      // Only perform real-time validation if the user has typed something.
      // If the field is cleared, simply remove the error message.
      if (!value) {
        setErrors(prev => {
          const { password, ...rest } = prev;
          return rest;
        });
      } else {
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName
        };
        const validation = validatePassword(value, userData);
        if (!validation.isValid) {
          setErrors(prev => ({ ...prev, password: validation.error }));
        } else {
          setErrors(prev => {
            const { password, ...rest } = prev;
            return rest;
          });
        }
      }
    }

    // 4. City Validation (Alpha only)
    if (name === 'city') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // 5. Status Change Detection
    if (name === 'status' && isEditMode) {
      const currentStatus = formData.status;
      if (currentStatus === 'Active' && value === 'Inactive') {
        startInactivationFlow();
        // Keep status unchanged until reassignment/no-patient flow completes.
        return;
      }
    }

    // Username validation (letters, numbers, ., _)
    if (name === 'username') {
      const cleanedValue = sanitizeUsernameInput(value);
      setFormData(prev => ({ ...prev, username: cleanedValue }));
      const formatError = getUsernameFormatError(cleanedValue);
      if (!cleanedValue) {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      } else if (formatError) {
        setErrors(prev => ({ ...prev, username: formatError }));
      } else {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
        scheduleUsernameCheck(cleanedValue, setErrors);
      }
      return;
    }

    // Email Validation (Real-time + Character Filtering)
    if (name === 'email') {
      const cleanedValue = sanitizeEmailInput(value);
      setFormData(prev => ({ ...prev, email: cleanedValue }));

      const emailErr = getEmailValidationError(cleanedValue);
      if (emailErr) {
        setErrors(prev => ({ ...prev, email: emailErr }));
      } else {
        setErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
        scheduleEmailCheck(cleanedValue, setErrors);
      }
      return;
    }

    if (name === 'confirmPassword') {
      if (!value && !isEditMode) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.password && value !== formData.password) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else {
        delete newErrors.confirmPassword;
      }
    }

    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username',
      addressLine1: 'Address Line 1',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      timeZone: 'Time Zone',
      twoWayAuth: 'Two Way Authentication'
    };

    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // If state name changed, sync stateId
      if (name === 'state') {
        const selectedState = US_STATES.find(s => s.name === value);
        if (selectedState) {
          updated.stateId = selectedState.id;
        }
      }
      
      return updated;
    });

    if (name !== 'password' && name !== 'email') {
      updateFieldErrorOnChange(setErrors, name, value, {
        required: Boolean(fieldLabels[name]),
        requiredMessage: fieldLabels[name] ? `${fieldLabels[name]} is required` : null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Helper function to safely trim string values
    const safeTrim = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    };

    // Required fields validation
    if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';
    // if (!formData.timeZone) newErrors.timeZone = 'Time Zone is required';

    // Username & password validation
    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else {
      const formatError = getUsernameFormatError(formData.username);
      if (formatError) newErrors.username = formatError;
    }

    if (!isEditMode) {
      if (!formData.password?.trim()) {
        newErrors.password = 'Password is required';
      } else {
        const userData = { firstName: formData.firstName, lastName: formData.lastName };
        const validation = validatePassword(formData.password, userData);
        if (!validation.isValid) newErrors.password = validation.error;
      }
    } else if (formData.password?.trim()) {
      const userData = { firstName: formData.firstName, lastName: formData.lastName };
      const validation = validatePassword(formData.password, userData);
      if (!validation.isValid) newErrors.password = validation.error;
    }

    if (!isEditMode && !formData.confirmPassword?.trim()) {
      newErrors.confirmPassword = 'Confirm Password is required';
    }

    if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    
    // City alpha validation
    if (!formData.city?.trim()) {
      newErrors.city = 'City is required';
    } else if (!/^[a-zA-Z\s]*$/.test(formData.city)) {
      newErrors.city = 'City can only contain alphabets';
    }

    if (!formData.state?.trim()) newErrors.state = 'State is required';
    
    // Zip Code
    if (!formData.zipCode?.trim()) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (formData.zipCode.length !== 5) {
      newErrors.zipCode = 'Zip code must be exactly 5 digits';
    }

    // Email validation (trim extra spaces)
    const emailErr = getEmailValidationError((formData.email || '').trim());
    if (emailErr) newErrors.email = emailErr;

    // Phone validation (fully filled)
    const homePhoneError = validateRequiredPhone(formData.homePhone, { required: false, label: 'Home Phone' });
    if (homePhoneError) newErrors.homePhone = homePhoneError;

    const cellPhoneError = validateRequiredPhone(formData.cellPhone, { label: 'Cell Phone' });
    if (cellPhoneError) newErrors.cellPhone = cellPhoneError;

    const faxError = validateRequiredPhone(formData.fax, { required: false, label: 'Fax' });
    if (faxError) newErrors.fax = faxError;

    // if (!formData.twoWayAuth?.trim()) newErrors.twoWayAuth = 'Two Way Authentication is required';
    // if (!formData.timeZone?.trim()) newErrors.timeZone = 'Time Zone is required';
    if (!formData.status?.trim()) newErrors.status = 'Status is required';

    // Password confirmation
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    const mergedErrors = preserveAvailabilityErrors(errors, newErrors);
    setErrors(mergedErrors);
    return Object.keys(mergedErrors).length === 0;
  };

  // Debug mount
  useEffect(() => {
  }, []);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);

    if (validateForm()) {
      const usernameOk = await ensureUsernameAvailable(formData.username, setErrors);
      const emailOk = await ensureEmailAvailable(formData.email, setErrors);
      if (!usernameOk || !emailOk) {
        setIsSubmitting(false);
        toast.warn(VALIDATION_SUMMARY_MESSAGE);
        return;
      }
      try {
        await saveProvider();
      } catch (error) {
        console.error('Submit catch:', error);
        if (applyPasswordServerFieldError(setErrors, error)) {
          return;
        }
        const errorMessage = getProviderSubmissionErrorMessage(error, '');
        if (errorMessage.toLowerCase().includes('missing') || errorMessage.toLowerCase().includes('required')) {
          toast.warn(VALIDATION_SUMMARY_MESSAGE);
        } else {
          toast.error(errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
      toast.warn(VALIDATION_SUMMARY_MESSAGE);
    }

  };
  const saveProvider = async () => {
    const providerData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      middle_name: formData.middleInitial,
      email: (formData.email || '').trim(),
      phone: (formData.cellPhone || '').replace(/\D/g, ''),
      cell_phone: (formData.homePhone || '').replace(/\D/g, ''),
      fax: formData.fax,
      address_line_1: formData.addressLine1,
      address_line_2: formData.addressLine2,
      city: formData.city,
      zip_code: formData.zipCode,
      username: formData.username.trim(),
      status: formData.status || 'Active',
      is_two_way_auth: formData.twoWayAuth === 'Enabled' ? 1 : 0,
      mobile_app_access: formData.mobileAppAccess === 'Enabled' ? 1 : 0,
      time_zone: formData.timeZone || 'EST - Eastern Time',
      state_id: formData.stateId || 1,
      title_id: formData.title ? parseInt(formData.title) : null,
      password: formData.password?.trim() || undefined
    };

    if (isEditMode && !providerData.password) {
      delete providerData.password;
    }
    try {
      if (isEditMode) {
        await dispatch(updateProvider({ practiceId, providerId: editId, providerData })).unwrap();
        toast.success('Provider updated successfully');
      } else {
        const tempId = `temp-provider-${Date.now()}`;
        const optimisticProvider = {
          id: tempId,
          name: [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim() || 'N/A',
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: (formData.email || '').trim(),
          home_phone: formData.cellPhone || 'N/A',
          cell_phone: formData.homePhone || 'N/A',
          fax: formData.fax || 'N/A',
          status: formData.status || 'Active',
          address: {
            address_line_1: formData.addressLine1 || 'N/A'
          }
        };

        dispatch(addProviderOptimistic({ practiceId, provider: optimisticProvider }));
        await dispatch(createProvider({ practiceId, providerData, tempId })).unwrap();
        toast.success('Provider added successfully');
      }
      savedSuccessfullyRef.current = true;
      const fromDashboard = location.state?.fromDashboard;
      const path = fromDashboard ? `/dashboard/${practiceId}?tab=provider` : `/practices/${practiceId}?tab=provider`;
      navigate(path, { state: { fromDashboard } });
    } catch (error) {

      console.error('Submission error:', error);
      throw error; // Re-throw to be caught by handleSubmit
    }
  };


  const startInactivationFlow = async () => {
    setIsSubmitting(true);
    try {
      // 1. Fetch patients for this provider
      const patientsResponse = await api.get(`/practices/${practiceId}/patients`, { 
        params: { providerId: editId, limit: 100 } 
      });
      const patients = patientsResponse.data?.data?.patients || [];
      setAssignedPatients(patients);

      // 2. Fetch alternative providers
      const providersResponse = await api.get(`/practices/${practiceId}/providers`, { params: { limit: 1000 } });
      const rawProviders =
        providersResponse.data?.data?.providers ||
        providersResponse.data?.providers ||
        providersResponse.data?.data ||
        providersResponse.data ||
        [];
      const providers = Array.isArray(rawProviders) ? rawProviders : [];
      
      // Filter out the current provider
      setAlternativeProviders(
        providers.filter((p) => {
          const status = p?.status;
          const isActive =
            status === 1 ||
            status === true ||
            (typeof status === 'string' && status.toLowerCase() === 'active');
          return String(p?.id) !== String(editId) && isActive;
        })
      );

      if (patients.length > 0) {
        // Initialize reassignments state
        const initialReassignments = {};
        patients.forEach(p => {
          initialReassignments[p.id] = '';
        });
        setReassignments(initialReassignments);
        setShowReassignmentModal(true);
      } else {
        // No patients, proceed with inactivation
        setFormData(prev => ({ ...prev, status: 'Inactive' }));
        toast.info('No patients assigned. You can now save the provider as Inactive.');
      }
    } catch (error) {
      console.error('Failed to start inactivation flow:', error);
      toast.error('Failed to check assigned patients.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchReassign = async () => {
    // Validate that all patients have a new provider selected
    const unassignedCount = Object.values(reassignments).filter(id => !id).length;
    if (unassignedCount > 0) {
      toast.warning(`Please select a new provider for all ${unassignedCount} patients.`);
      return;
    }

    setIsReassigning(true);
    try {
      const payload = {
        reassignments: Object.entries(reassignments).map(([patientId, providerId]) => ({
          patientId,
          providerId
        }))
      };

      await api.post(`/practices/${practiceId}/patients/batch-reassign-provider`, payload);
      
      setShowReassignmentModal(false);
      setFormData(prev => ({ ...prev, status: 'Inactive' }));
      // Refresh practice patient list immediately so patient table shows updated provider names without full reload.
      dispatch(fetchPracticePatients({ id: practiceId, params: { page: 1, limit: 1000 } }));
      toast.success('Patients reassigned successfully. Provider status set to Inactive.');
    } catch (error) {
      console.error('Batch reassignment failed:', error);
      toast.error('Failed to reassign patients.');
    } finally {
      setIsReassigning(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCancel = async () => {
    if (isEditMode && editId && practiceId && !savedSuccessfullyRef.current) {
      try {
        await providerService.logProviderView(practiceId, editId);
      } catch (error) {
        console.error('Failed to log provider view:', error);
      }
    }
    const fromDashboard = location.state?.fromDashboard;
    const path = fromDashboard ? `/dashboard/${practiceId}?tab=provider` : `/practices/${practiceId}?tab=provider`;
    navigate(path, { state: { fromDashboard } });
  };


  // Show loading state while fetching provider data in edit mode
  if (isEditMode && isLoading) {
    return (
      <div className="ap-container">
        <div className="add-patient-form-card" style={{ padding: '16px', display: 'grid', gap: '14px' }}>
          <FormDetailSkeleton titleLines={1} fieldRows={8} columns={2} showFooter={false} />
          <FormDetailSkeleton titleLines={1} fieldRows={2} columns={2} showFooter={true} />
        </div>
      </div>
    );
  }

  // Get logo URL - construct full URL if logo path exists
  const getLogoUrl = () => {
    if (!currentPractice?.practice_logo) return null;
    const logo = currentPractice.practice_logo;
    if (logo.startsWith('http://') || logo.startsWith('https://')) return logo;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const baseUrl = API_URL.replace('/api', '');
    const logoPath = logo.startsWith('/') ? logo : `/${logo}`;
    return `${baseUrl}${logoPath}`;
  };

  return (
    <div className="ap-container">
      <AddFormModel
        title={isEditMode ? 'Edit Provider' : 'Add Provider'}
        currentPractice={currentPractice}
        getLogoUrl={getLogoUrl}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        leftSection={
          <div className="ap-form-redesign-wrapper">
            <div className="pif-demo-section-label">Personal Information</div>

            {/* Title | First Name */}
            <div className="ap-row ap-row-title-first">
              <div className="ap-field w-title">
                <label htmlFor="title">Title</label>
                <select id="title" name="title" value={formData.title} onChange={handleInputChange}>
                  <option value="">- Select -</option>
                  {TITLES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                {errors.title && <span className="error-text">{errors.title}</span>}
              </div>
              <div className="ap-field w-first-name">
                <label htmlFor="firstName">First Name <span className="required">*</span></label>
                 <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="e.g., John" required />
                {errors.firstName && <span className="error-text">{errors.firstName}</span>}
              </div>
            </div>

            {/* M I | Last Name */}
            <div className="ap-row ap-row-mi-last">
              <div className="ap-field w-mi">
                <label htmlFor="middleInitial">Middle Name</label>
                 <input type="text" id="middleInitial" name="middleInitial" value={formData.middleInitial} onChange={handleInputChange} maxLength={20} placeholder="e.g., Marie" />
               </div>
               <div className="ap-field w-last-name">
                 <label htmlFor="lastName">Last Name <span className="required">*</span></label>
                 <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="e.g., Doe" required />
                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
              </div>
            </div>

            {/* Home Phone | Cell Phone | Fax */}
            <div className="ap-row ap-row-3col">
              <div className="ap-field">
                <label htmlFor="homePhone">Home Phone</label>
                <PhoneNumberInput id="homePhone" name="homePhone" value={formData.homePhone} onChange={handleInputChange} placeholder="123-231-2345" />
                {errors.homePhone && <span className="error-text">{errors.homePhone}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="cellPhone">Cell Phone <span className="required">*</span></label>
                <PhoneNumberInput id="cellPhone" name="cellPhone" value={formData.cellPhone} onChange={handleInputChange} placeholder="123-231-2345" required />
                {errors.cellPhone && <span className="error-text">{errors.cellPhone}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="fax">Fax</label>
                <PhoneNumberInput id="fax" name="fax" value={formData.fax} onChange={handleInputChange} placeholder="123-231-2345" />
                {errors.fax && <span className="error-text">{errors.fax}</span>}
              </div>
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Account Credentials</div>

            {/* Username | Email */}
            <div className="ap-row ap-row-2col">
              <div className="ap-field">
                <label htmlFor="username">Username <span className="required">*</span></label>
                <input type="text" id="username" name="username" value={formData.username} onChange={handleInputChange} onPaste={(e) => handleUsernamePaste(e, setErrors)} onBlur={() => handleUsernameBlur(formData.username, setErrors)} autoComplete="username" placeholder="e.g., john.doe" maxLength={USERNAME_MAX_LENGTH} />
                {errors.username && <span className="error-text">{errors.username}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="email">Email <span className="required">*</span></label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} onPaste={(e) => handleEmailPaste(e, setErrors)} placeholder="e.g., provider@example.com" required />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>

            {/* Password | Confirm Password */}
            <div className="ap-row ap-row-2col">
              <div className="ap-field">
                <label htmlFor="password">Password {!isEditMode && <span className="required">*</span>}</label>
                <div className="ap-password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    placeholder={isEditMode ? 'Leave blank to keep current' : 'Enter at least 8 characters'}
                  />
                  <button type="button" className="ap-password-toggle" onClick={togglePasswordVisibility} aria-label="Toggle password visibility">
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {errors.password && <span className="error-text">{errors.password}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="confirmPassword">Confirm Password {!isEditMode && <span className="required">*</span>}</label>
                <div className="ap-password-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    placeholder={isEditMode ? 'Leave blank to keep current' : 'Enter at least 8 characters'}
                  />
                  <button type="button" className="ap-password-toggle" onClick={toggleConfirmPasswordVisibility} aria-label="Toggle confirm password visibility">
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
              </div>
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Address & Location</div>

            {/* Address Line 1 */}
            <div className="ap-field full-width">
              <label htmlFor="addressLine1">Address Line 1 <span className="required">*</span></label>
               <input type="text" id="addressLine1" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} placeholder="e.g., 123 Main St" required />
               {errors.addressLine1 && <span className="error-text">{errors.addressLine1}</span>}
             </div>
 
             {/* Address Line 2 */}
             <div className="ap-field full-width">
               <label htmlFor="addressLine2">Address Line 2</label>
               <input type="text" id="addressLine2" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} placeholder="e.g., Apt 4B" />
            </div>

            {/* City | State | Zip Code */}
            <div className="ap-row ap-row-3col">
              <div className="ap-field">
                <label htmlFor="city">City <span className="required">*</span></label>
                 <input type="text" id="city" name="city" value={formData.city} onChange={handleInputChange} placeholder="e.g., New York" required />
                {errors.city && <span className="error-text">{errors.city}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="state">State <span className="required">*</span></label>
                <select id="state" name="state" value={formData.state} onChange={handleInputChange} required>
                  <option value="">Select State</option>
                  {US_STATES.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
                {errors.state && <span className="error-text">{errors.state}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="zipCode">Zip Code <span className="required">*</span></label>
                  <input type="text" id="zipCode" name="zipCode" value={formData.zipCode} onChange={handleInputChange} placeholder="e.g., 10001" maxLength="5" required />
                {errors.zipCode && <span className="error-text">{errors.zipCode}</span>}
              </div>
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Additional Settings</div>

            {/* Status & Settings */}
            <div className="ap-row ap-row-3col">
              <div className="ap-field">
                <label htmlFor="status">Status <span className="required">*</span></label>
                <select id="status" name="status" value={formData.status || 'Active'} onChange={handleInputChange}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                {errors.status && <span className="error-text">{errors.status}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="twoWayAuth">Two Way Authentication <span className="required">*</span></label>
                <select id="twoWayAuth" name="twoWayAuth" value={formData.twoWayAuth || 'Disabled'} onChange={handleInputChange}>
                  <option value="Disabled">Disabled</option>
                  <option value="Enabled">Enabled</option>
                </select>
                {errors.twoWayAuth && <span className="error-text">{errors.twoWayAuth}</span>}
              </div>
              <div className="ap-field">
                <label htmlFor="mobileAppAccess">Mobile App Access</label>
                <select id="mobileAppAccess" name="mobileAppAccess" value={formData.mobileAppAccess || 'Disabled'} onChange={handleInputChange}>
                  <option value="Disabled">Off</option>
                  <option value="Enabled">On</option>
                </select>
              </div>
            </div>
          </div>
        }
        rightSection={null}
        footerActions={
          <>
            {isEditMode ? (
              <button
                type="button"
                className="btn-submit"
                onClick={() => navigate(`/event-logs/Provider/${editId}?practiceId=${practiceId || ''}`)}
                title="View Change History"
                style={{ width: 'auto', paddingLeft: '15px', paddingRight: '15px', backgroundColor: 'var(--ev-navy-dark)', marginRight: 'auto', height: '40px' }}
              >
                <FontAwesomeIcon icon={faList} className="me-2" /> Event Logs
              </button>
            ) : (
              <div style={{ marginRight: 'auto' }}></div>
            )}
            <div className="add-form-actions-wrap">
              <FormActionButtons
                onCancel={handleCancel}
                disabled={isSubmitting || isLoading || isUpdating || isCreating || (isEditMode && !isChanged)}
                isSubmitting={isSubmitting || isUpdating || isCreating}
                isEditMode={isEditMode}
                addLabel="Add Providers"
                updateLabel="Update Providers"
              />
            </div>

          </>
        }
      />

      {/* Validation Warning Modal */}
      <Modal 
        show={showValidationModal} 
        onHide={() => setShowValidationModal(false)} 
        centered 
        className="add-provider-validation-modal"
      >
        <Modal.Body className="add-provider-modal-body" style={{ padding: '30px', textAlign: 'center' }}>
          <div className="add-provider-modal-icon-wrapper" style={{ marginBottom: '20px' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="add-provider-modal-icon" style={{ fontSize: '40px', color: '#1A252F' }} />
          </div>
          <h4 className="add-provider-modal-title" style={{ color: '#1A252F', marginBottom: '20px', fontWeight: '600' }}>Fill all required fields</h4>
          <button 
            type="button"
            className="add-provider-modal-btn" 
            onClick={() => setShowValidationModal(false)}
            style={{ backgroundColor: '#1A252F', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '4px', cursor: 'pointer', width: '100%' }}
          >
            OK
          </button>
        </Modal.Body>
      </Modal>

      <ReassignmentModal
        show={showReassignmentModal}
        onClose={() => {
          setShowReassignmentModal(false);
          setFormData((prev) => ({ ...prev, status: 'Active' }));
        }}
        title="Reassign Patients"
        description={`Select a new provider for each of the ${assignedPatients.length} patient(s) before inactivation.`}
        patients={assignedPatients}
        alternatives={alternativeProviders}
        reassignments={reassignments}
        onReassignmentChange={(patientId, providerId) =>
          setReassignments((prev) => ({ ...prev, [patientId]: providerId }))
        }
        onAssignAll={(providerId) =>
          setReassignments((prev) => {
            const next = { ...prev };
            assignedPatients.forEach((patient) => {
              next[patient.id] = providerId;
            });
            return next;
          })
        }
        onSave={handleBatchReassign}
        isSaving={isReassigning}
        saveLabel="Save & Inactivate"
        optionPlaceholder="Select provider..."
        currentLabel="Current caregiver/provider"
        reassignToLabel="Reassign to provider"
        getCurrentAssignee={(patient) =>
          patient.provider_name ||
          patient.caregiver_name ||
          patient.assigned_to_name ||
          patient.current_provider_name ||
          ((patient.provider_first_name || patient.provider_last_name)
            ? `${patient.provider_first_name || ''} ${patient.provider_last_name || ''}`.trim()
            : '') ||
          ((patient.caregiver_first_name || patient.caregiver_last_name)
            ? `${patient.caregiver_first_name || ''} ${patient.caregiver_last_name || ''}`.trim()
            : '') ||
          'N/A'
        }
      />
    </div>
  );

};
export default AddProvider;
